<template>
  <AppShell>
    <section class="view on soa-view-sites">
      <div class="pagehead"><div><h1>{{ t("sites_h") }}</h1><p class="sub">{{ t("sites_sub") }}</p></div></div>
      <p v-if="loading" class="empty" role="status">{{ t("loading") }}</p>
      <p v-else-if="error" class="empty" role="alert">{{ error }} <button type="button" class="atbtn" @click="load">Retry</button></p>
      <div v-else-if="!sites.length" class="sites-empty"><h2 class="se-ttl">{{ t("sites_empty_h") }}</h2><p class="se-sub">{{ t("sites_empty_sub") }}</p><button type="button" class="btn" @click="goMaya">{{ t("sites_empty_cta") }}</button></div>
      <div v-else class="grid">
        <article v-for="site in sites" :key="site.brand_id" class="card sitecard" :class="{ 'sc-ov': site.status !== 'none' }" :style="{ '--fc': statusColor(site.status) }">
          <div v-if="site.status !== 'none'" class="sc-bg" aria-hidden="true"><iframe :src="previewUrl(site.brand_id)" loading="lazy" tabindex="-1" scrolling="no" title=""></iframe></div><div v-if="site.status !== 'none'" class="sc-scrim" aria-hidden="true"></div>
          <div class="sc-body"><div class="sc-top"><strong class="sc-brand">{{ site.brand }}</strong><span class="sc-pill">{{ statusLabel(site.status) }}</span></div><p class="sc-meta">{{ siteMeta(site) }}</p><div class="sc-acts"><button v-if="site.status === 'none'" type="button" class="btn" @click="startBuild(site)">{{ t("site_build_cta") }}</button><button v-else type="button" class="btn" @click="openWorkbench(site)">{{ t("site_open_studio") }}</button><a v-if="site.status === 'published' && site.live_url" class="btn ghost" :href="site.live_url" target="_blank" rel="noopener">{{ t("site_view_live") }} <span aria-hidden="true">↗</span><span class="sr-only"> (opens in new window)</span></a><!-- GEO_EXEC_R9: one custom domain per published site; hidden while the switch is off --><button v-if="domainsOn && site.status === 'published' && site.site_id" type="button" class="btn ghost" @click="openDomain(site)">{{ t("cd_cta") }}</button></div></div>
        </article>
      </div>
      <!-- FE-5 · the comparison pages the Pages page used to hold. They are part of the GEO site,
           so they live under it; `/pages?…` redirects here with `?section=comparisons`. -->
      <ComparisonMatrix v-if="!loading && !error" anchor="comparisons" />
      <CustomDomainPanel v-if="domainSite" :site="domainSite" @close="closeDomain" />
    </section>
  </AppShell>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

import { apiPrefix } from "../api/client";
import { getSiteDomain } from "../api/geoExecR9";  // GEO_EXEC_R9
import { listSites } from "../api/sites";
import AppShell from "../layout/AppShell.vue";
import ComparisonMatrix from "../components/ComparisonMatrix.vue";  // FE-5
import CustomDomainPanel from "../components/CustomDomainPanel.vue";
import { featureOn, probeFeature } from "../stores/geoExecFeatures";
import { selectBrand } from "../stores/brands";
import { useI18n } from "../useI18n";

const router = useRouter();
const route = useRoute();
const { t } = useI18n();
const sites = ref([]), loading = ref(true), error = ref("");
const domainSite = ref(null);
// GEO_EXEC_R9 · the fourth button on a published site card. Probed once with a real read: the
// route answers 404 {"error": "not found"} while the switch is off, and 200 otherwise.
const domainsOn = computed(() => featureOn("r9"));
let controller;

function openDomain(site) { domainSite.value = site; }
function closeDomain() { domainSite.value = null; }
function statusColor(status) { return { published: "var(--jade)", draft: "var(--amber)", none: "var(--mute)" }[status] || "var(--mute)"; }
function statusLabel(status) { return t({ published: "site_st_pub", draft: "site_st_draft", none: "site_st_none" }[status] || "site_st_none"); }
function siteMeta(site) { if (site.status === "none") return t("site_none_note"); const values = [`${site.pages || 0} ${t("site_pages")}`]; if (site.status === "published" && site.live_url) values.push(String(site.live_url).replace(/^https?:\/\//, "").replace(/\/$/, "")); return values.join(" · "); }
function previewUrl(brandId) { return `${apiPrefix()}/api/site/preview?brand_id=${encodeURIComponent(brandId)}&page=home`; }
async function load() { controller?.abort(); controller = new AbortController(); loading.value = true; error.value = ""; try { const data = await listSites({ signal: controller.signal }); sites.value = data.sites || []; const probe = sites.value.find((site) => site.site_id); if (probe) probeFeature("r9", () => getSiteDomain(probe.site_id)); } catch (caught) { if (caught.name !== "AbortError") error.value = caught.message || "Unable to load sites."; } finally { loading.value = false; } }
function goMaya() { router.push("/maya"); }
function openWorkbench(site) {
  selectBrand(site.brand_id);
  router.push({ path: "/maya", query: { wb: site.brand_id } });
}
function startBuild(site) { try { sessionStorage.setItem("soa_site_build_handoff", JSON.stringify({ brand: site.brand || "", brandId: site.brand_id })); } catch { /* Maya remains usable without storage. */ } goMaya(); }
// An old `/pages` link arrives as `?section=comparisons`. Scroll to the block once the cards have
// laid out, so the client lands on the thing they clicked rather than at the top of Sites.
function revealSection() {
  if (String(route.query.section || "") !== "comparisons") return;
  nextTick(() => {
    const target = document.getElementById("comparisons");
    if (!target) return;
    target.scrollIntoView({ behavior: window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth", block: "start" });
  });
}

onMounted(async () => {
  await load();
  revealSection();
});
// `/sites` → `/sites?section=comparisons` (an old /pages link followed from inside the app) reuses
// this component, so mount never runs again; the query is what changed, so watch the query.
watch(() => route.query.section, revealSection);
onBeforeUnmount(() => controller?.abort());
</script>
